import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../../contexts/CartContext';

const peptides = [
  {
    id: 'peptide-bpc157',
    name: 'BPC-157',
    subtitle: 'Body Protection Compound',
    price: '$199',
    period: 'per vial',
    description: 'Accelerates healing of tendons, ligaments, muscles, and gut lining. Reduces inflammation and supports recovery from injury.',
    benefits: ['Tissue repair', 'Gut healing', 'Anti-inflammatory', 'Joint support'],
    icon: 'ri-heart-pulse-line',
    badge: 'Recovery',
  },
  {
    id: 'peptide-tb500',
    name: 'TB-500',
    subtitle: 'Thymosin Beta-4',
    price: '$249',
    period: 'per vial',
    description: 'Promotes cell migration and proliferation, accelerates wound healing, and reduces inflammation throughout the body.',
    benefits: ['Wound healing', 'Muscle repair', 'Flexibility', 'Reduced scarring'],
    icon: 'ri-shield-flash-line',
    badge: 'Healing',
  },
  {
    id: 'peptide-sermorelin',
    name: 'Sermorelin',
    subtitle: 'Growth Hormone Releasing',
    price: '$299',
    period: 'per vial',
    description: 'Stimulates natural growth hormone production for improved body composition, sleep quality, and anti-aging benefits.',
    benefits: ['HGH stimulation', 'Fat loss', 'Muscle gain', 'Better sleep'],
    icon: 'ri-seedling-line',
    badge: 'Anti-Aging',
  },
  {
    id: 'peptide-ipamorelin',
    name: 'Ipamorelin',
    subtitle: 'Growth Hormone Secretagogue',
    price: '$279',
    period: 'per vial',
    description: 'Selectively stimulates growth hormone release with minimal side effects. Ideal for body recomposition and longevity.',
    benefits: ['Lean muscle', 'Fat reduction', 'Recovery', 'Longevity'],
    icon: 'ri-body-scan-line',
    badge: 'Performance',
  },
  {
    id: 'peptide-cjc1295',
    name: 'CJC-1295',
    subtitle: 'Growth Hormone Releasing Hormone',
    price: '$319',
    period: 'per vial',
    description: 'Long-acting GHRH analog that sustains elevated growth hormone levels for extended anabolic and recovery benefits.',
    benefits: ['Sustained HGH', 'Deep sleep', 'Muscle synthesis', 'Anti-aging'],
    icon: 'ri-time-line',
    badge: 'Longevity',
  },
  {
    id: 'peptide-pt141',
    name: 'PT-141',
    subtitle: 'Bremelanotide',
    price: '$229',
    period: 'per vial',
    description: 'Activates melanocortin receptors to enhance libido and sexual function in both men and women. Clinician-supervised.',
    benefits: ['Libido support', 'Sexual wellness', 'Mood enhancement', 'Energy'],
    icon: 'ri-fire-line',
    badge: 'Wellness',
  },
];

const badgeColors: Record<string, string> = {
  Recovery: 'bg-amber-50 text-amber-700 border-amber-200',
  Healing: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Anti-Aging': 'bg-rose-50 text-rose-700 border-rose-200',
  Performance: 'bg-blue-50 text-blue-700 border-blue-200',
  Longevity: 'bg-violet-50 text-violet-700 border-violet-200',
  Wellness: 'bg-orange-50 text-orange-700 border-orange-200',
};

export default function PeptidesSection() {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());

  const handleAddToCart = (peptide: typeof peptides[0]) => {
    addToCart({
      id: peptide.id,
      name: peptide.name,
      type: 'vitamin',
      ivCount: peptide.subtitle,
      price: peptide.price,
      period: peptide.period,
      features: peptide.benefits,
    });
    setAddedIds(prev => new Set(prev).add(peptide.id));
    setTimeout(() => navigate('/cart'), 600);
  };

  return (
    <section className="py-24 px-6 lg:px-12 bg-bone">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1 bg-gold/20 text-gold text-xs tracking-widest uppercase rounded-full mb-5">
            Advanced Therapeutics
          </span>
          <h2 className="text-4xl md:text-5xl font-light text-charcoal tracking-wide mb-5">
            Peptide Therapy
          </h2>
          <div className="w-16 h-px bg-gold mx-auto mb-6"></div>
          <p className="text-greige text-lg font-light leading-relaxed max-w-2xl mx-auto">
            Clinician-supervised peptide protocols for recovery, performance, anti-aging, and total-body optimization. All administered by licensed RNs.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {peptides.map((peptide) => {
            const isAdded = addedIds.has(peptide.id);
            return (
              <div
                key={peptide.id}
                className="bg-white border border-[#e8e2da] rounded-2xl overflow-hidden group hover:-translate-y-1 transition-all duration-400 flex flex-col"
              >
                {/* Top accent bar */}
                <div className="h-1 bg-gradient-to-r from-gold/60 via-gold to-gold/60"></div>

                <div className="p-8 flex flex-col flex-1">
                  {/* Badge + Icon row */}
                  <div className="flex items-center justify-between mb-6">
                    <span className={`text-[10px] font-light tracking-widest uppercase px-3 py-1 rounded-full border ${badgeColors[peptide.badge]}`}>
                      {peptide.badge}
                    </span>
                    <div className="w-10 h-10 flex items-center justify-center bg-gold/10 rounded-full">
                      <i className={`${peptide.icon} text-xl text-gold`}></i>
                    </div>
                  </div>

                  {/* Name */}
                  <h3 className="text-2xl font-light text-charcoal tracking-wide mb-1">
                    {peptide.name}
                  </h3>
                  <p className="text-xs text-greige font-light tracking-widest uppercase mb-4">
                    {peptide.subtitle}
                  </p>

                  {/* Description */}
                  <p className="text-greige text-sm font-light leading-relaxed mb-6 flex-1">
                    {peptide.description}
                  </p>

                  {/* Benefits */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {peptide.benefits.map((b) => (
                      <span
                        key={b}
                        className="text-[10px] font-light tracking-wide text-charcoal/70 bg-[#f5f2ee] px-3 py-1 rounded-full"
                      >
                        {b}
                      </span>
                    ))}
                  </div>

                  {/* Price + CTA */}
                  <div className="flex items-center justify-between mt-auto">
                    <div>
                      <p className="text-2xl font-light text-charcoal">{peptide.price}</p>
                      <p className="text-xs text-greige font-light">{peptide.period}</p>
                    </div>
                    <button
                      onClick={() => handleAddToCart(peptide)}
                      disabled={isAdded}
                      className={`px-6 py-2.5 text-xs font-light tracking-[0.15em] uppercase rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${
                        isAdded
                          ? 'bg-gold text-white'
                          : 'border border-charcoal text-charcoal hover:bg-charcoal hover:text-white'
                      }`}
                    >
                      {isAdded ? (
                        <span className="flex items-center gap-1.5">
                          <i className="ri-check-line"></i> Added
                        </span>
                      ) : (
                        'Add to Cart'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-center text-greige text-xs font-light mt-10 max-w-2xl mx-auto leading-relaxed">
          All peptide therapies are prescribed and supervised by licensed clinicians. Individual results may vary. A consultation is required prior to treatment.
        </p>
      </div>
    </section>
  );
}
